import { Link } from '@tanstack/react-router';
import { AlertTriangle, CheckCircle2, Circle } from 'lucide-react';
import { useState } from 'react';
import { Empty, QueryView } from '../components/states';
import { PageHeader } from '../components/page';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select } from '../components/ui/input';
import { day, instant, money } from '../lib/format';
import { useAuth } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import type { Messages } from '../i18n/fr';
import { useApiQuery } from '../lib/query';
import { useReferentials, useTenant } from '../lib/referentials';
import type { Dashboard } from '../lib/types';
import { WeeklyChart } from '../components/weekly-chart';
import { PilotageSection } from './pilotage';

// Tableau de bord (BA-025, spec 08 §1).

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardContent>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
        {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      </CardContent>
    </Card>
  );
}

interface Onboarding {
  steps: { key: keyof Messages['onboarding']['steps']; done: boolean }[];
  completed: number;
  total: number;
  sandbox: boolean;
}

/** Liste de démarrage (BA-063), affichée aux administrateurs tant qu'elle n'est pas terminée. */
function OnboardingChecklist() {
  const t = useI18n().t;
  const admin = useAuth().can('administer');
  const query = useApiQuery<Onboarding>(admin ? '/v1/tenant/onboarding' : null);
  const data = query.data;
  if (!data || (data.completed === data.total && !data.sandbox)) return null;
  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle>
          {t.onboarding.title} ({data.completed}/{data.total})
        </CardTitle>
        {data.sandbox ? <span className="text-sm text-warning">{t.onboarding.sandbox}</span> : null}
      </CardHeader>
      <CardContent>
        <ul className="grid gap-2 sm:grid-cols-2">
          {data.steps.map((s) => (
            <li key={s.key} className="flex items-center gap-2 text-sm">
              {s.done ? (
                <CheckCircle2 className="h-4 w-4 text-success" aria-hidden />
              ) : (
                <Circle className="h-4 w-4 text-muted-foreground" aria-hidden />
              )}
              <span className={s.done ? 'text-muted-foreground line-through' : undefined}>
                {t.onboarding.steps[s.key]}
              </span>
              <span className="sr-only">{s.done ? t.onboarding.done : t.onboarding.todo}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export function DashboardPage() {
  const { t, language, count } = useI18n();
  // Alerte traduite selon sa nature ; libellé de l'API pour une nature inconnue du portail.
  const alertText = (a: { kind: string; label: string; count?: number | undefined }) => {
    const label = t.dashboard.alertLabels[a.kind as keyof typeof t.dashboard.alertLabels];
    if (!label) return a.label;
    return typeof label === 'string' ? label : count(a.count ?? 0, label);
  };
  const refs = useReferentials();
  const tz = useTenant().data?.timezone;
  const [filters, setFilters] = useState({ insurerId: '', productLineId: '', agentId: '' });
  const query = useApiQuery<Dashboard>('/v1/dashboard', filters);

  const select = (
    key: keyof typeof filters,
    label: string,
    options: { id: string; label: string }[],
  ) => (
    <Select
      aria-label={label}
      className="w-auto"
      value={filters[key]}
      onChange={(e) => setFilters((f) => ({ ...f, [key]: e.target.value }))}
    >
      <option value="">
        {label} : {t.common.all}
      </option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.label}
        </option>
      ))}
    </Select>
  );

  return (
    <>
      <PageHeader
        title={t.dashboard.title}
        actions={
          <>
            {select(
              'insurerId',
              t.installments.insurer,
              refs.insurers.map((i) => ({ id: i.id, label: i.displayName })),
            )}
            {select(
              'productLineId',
              t.installments.product,
              refs.productLines.map((p) => ({ id: p.id, label: p.labelFr })),
            )}
            {select(
              'agentId',
              t.installments.agent,
              refs.agents.map((a) => ({ id: a.id, label: a.name })),
            )}
          </>
        }
      />
      <OnboardingChecklist />
      <QueryView query={query}>
        {(data) => (
          <div className="flex flex-col gap-4">
            {data.draftMode ? (
              <p
                role="status"
                className="flex items-center gap-2 rounded-lg border border-warning/30 bg-warning-soft p-3 text-sm text-warning"
              >
                <AlertTriangle className="h-4 w-4" aria-hidden />
                {t.dashboard.draftBanner}
              </p>
            ) : null}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              <Tile label={t.dashboard.dueNext30} value={money(data.tiles.dueNext30, language)} />
              <Tile
                label={t.dashboard.recovered30}
                value={money(data.tiles.recovered30, language)}
              />
              <Tile
                label={t.dashboard.paidBeforeDueRate}
                value={
                  data.tiles.paidBeforeDueRate === null
                    ? '-'
                    : `${Math.round(data.tiles.paidBeforeDueRate * 100)} %`
                }
              />
              <Tile
                label={t.dashboard.overdueOver15}
                value={money(data.tiles.overdueOver15, language)}
              />
              <Tile
                label={t.dashboard.activeSequences}
                value={String(data.tiles.activeSequences)}
              />
              <Tile
                label={t.dashboard.messagesThisMonth}
                value={String(data.tiles.messagesThisMonth.count)}
                hint={`${t.dashboard.messagesCost} : ${money(data.tiles.messagesThisMonth.costMillimes, language)}`}
              />
            </div>
            {data.weekly.length ? (
              <Card>
                <CardHeader>
                  <CardTitle>{t.dashboard.chart}</CardTitle>
                </CardHeader>
                <CardContent>
                  <WeeklyChart weeks={data.weekly} />
                </CardContent>
              </Card>
            ) : null}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>{t.dashboard.dueToday}</CardTitle>
                </CardHeader>
                {data.dueToday.length === 0 ? (
                  <Empty />
                ) : (
                  <ul className="divide-y divide-border">
                    {data.dueToday.map((d) => (
                      <li key={d.id} className="flex justify-between px-4 py-2 text-sm">
                        <Link
                          to="/echeances/$id"
                          params={{ id: d.id }}
                          className="text-primary hover:underline"
                        >
                          {d.policyNumber}
                        </Link>
                        <span className="tabular-nums">{money(d.amountDue, language)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>{t.dashboard.alerts}</CardTitle>
                </CardHeader>
                {data.alerts.length === 0 ? (
                  <Empty>{t.dashboard.noAlert}</Empty>
                ) : (
                  <ul className="divide-y divide-border">
                    {data.alerts.map((a) => (
                      <li key={a.kind} className="px-5 py-2.5 text-sm">
                        {alertText(a)}
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>{t.dashboard.openEscalations}</CardTitle>
                </CardHeader>
                {data.openEscalations.length === 0 ? (
                  <Empty />
                ) : (
                  <ul className="divide-y divide-border">
                    {data.openEscalations.map((e) => (
                      <li key={e.id} className="flex justify-between gap-2 px-4 py-2 text-sm">
                        {e.installmentId ? (
                          <Link
                            to="/echeances/$id"
                            params={{ id: e.installmentId }}
                            className="text-primary hover:underline"
                          >
                            {e.reason}
                          </Link>
                        ) : (
                          <span>{e.reason}</span>
                        )}
                        <span className="text-muted-foreground">{day(e.openedAt, language)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>{t.dashboard.lastPayments}</CardTitle>
                </CardHeader>
                {data.lastPayments.length === 0 ? (
                  <Empty />
                ) : (
                  <ul className="divide-y divide-border">
                    {data.lastPayments.map((p) => (
                      <li key={p.id} className="flex justify-between gap-2 px-4 py-2 text-sm">
                        <span>
                          {money(p.amount, language)}{' '}
                          <span className="text-muted-foreground">
                            (
                            {t.paymentMethods[p.method as keyof typeof t.paymentMethods] ??
                              p.method}
                            )
                          </span>
                        </span>
                        <span className="text-muted-foreground">
                          {instant(p.confirmedAt, tz, language)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>
          </div>
        )}
      </QueryView>
      <PilotageSection filters={filters} />
    </>
  );
}
